import {
  IngestedOrder,
  ReceivedOrder
} from '@model/ingested-order/ingested-order.model';
import { Region, Site, ExamName } from '@model/filter/filter-options.model';

/**
 * Backend DTO structure from actual API response
 * Endpoint: GET http://local.suranet.com:8081/preanalitica/admitted-orders
 * Backend can return examName, admissionSite, and region as string or object
 */
export interface IngestedOrderBackendDto {
  id: string;
  examCode: string;
  examName: string | ExamName;
  orderNumber: string;
  admissionDate: string; // ISO format: "2025-10-28T09:30:00"
  admissionSite: string | Site;
  region: string | Region;
  identificationType: string;
  identificationNumber: string;
  patientFirstName: string;
  patientLastName: string;
  patientFullName?: string;
  caseNumber?: string;
  permissions?: {
    canEdit: boolean;
    canDelete: boolean;
    canView: boolean;
  };
}

/**
 * Priority object from backend
 */
export interface Priority {
  id: number;
  name: string;
}

/**
 * Backend DTO structure from actual API response
 * Endpoint: GET /admitted-orders?status=RECEIVED
 * Backend can return nested fields as string or object
 */
export interface ReceivedOrderBackendDto {
  id: string;
  examCode: string;
  examName: string | ExamName;
  orderNumber: string;
  admissionDate: string;
  admissionSite?: string | Site;
  region?: string | Region;
  identificationType?: string;
  identificationNumber?: string;
  patientFirstName: string;
  patientLastName: string;
  birthDate?: string;
  priority: string | Priority;
  examType?: string | ExamName;
  isReprocess: boolean;
  reprocessCause?: string;
  reprocessObservation?: string;
  lgNumber: string;
  caseNumber: string;
  acmg?: boolean;
}

/**
 * Backend paginated response structure
 * Matches actual API response from /admitted-orders
 */
export interface IngestedOrderBackendResponse {
  content: IngestedOrderBackendDto[];
  pageableInfo: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

/**
 * Backend paginated response structure for received orders
 * Matches new API response format with nested objects
 */
export interface ReceivedOrderBackendResponse {
  content: ReceivedOrderBackendDto[];
  pageableInfo: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

/**
 * Mapper utility for Ingested Orders
 * Transforms backend DTOs to frontend domain models
 * Handles both old format (string) and new format (nested objects)
 */
export class IngestedOrdersMapper {
  /**
   * Maps backend DTO to frontend domain model
   * Converts ISO date string to Date object
   * Handles both string and nested object formats for examName, admissionSite, region
   *
   * @param dto Backend data transfer object from /admitted-orders
   * @returns Frontend domain model with Date object
   */
  static fromBackendDto(dto: IngestedOrderBackendDto): IngestedOrder {
    return {
      id: dto.id,
      examCode: dto.examCode,
      examName:
        typeof dto.examName === 'string' ? dto.examName : dto.examName.name,
      orderNumber: dto.orderNumber,
      admissionDate: new Date(dto.admissionDate), // Convert ISO string to Date
      admissionSite:
        typeof dto.admissionSite === 'string'
          ? dto.admissionSite
          : dto.admissionSite.name,
      region: typeof dto.region === 'string' ? dto.region : dto.region.name,
      identificationType: dto.identificationType,
      identificationNumber: dto.identificationNumber,
      patientFirstName: dto.patientFirstName,
      patientLastName: dto.patientLastName,
      patientFullName:
        dto.patientFullName ?? `${dto.patientFirstName} ${dto.patientLastName}`,
      caseNumber: dto.caseNumber ?? '',
      permissions: dto.permissions ?? {
        canEdit: true,
        canDelete: false,
        canView: true
      }
    };
  }

  /**
   * Maps array of backend DTOs to frontend models
   */
  static fromBackendDtoArray(dtos: IngestedOrderBackendDto[]): IngestedOrder[] {
    return dtos.map((dto) => this.fromBackendDto(dto));
  }
}

/**
 * Mapper utility for Received Orders (Pre-ingresos)
 * Transforms backend DTOs with nested objects to frontend domain models
 */
export class ReceivedOrdersMapper {
  /**
   * Maps backend DTO with nested objects to frontend domain model
   * Extracts name fields from nested objects
   *
   * @param dto Backend data transfer object from /admitted-orders?status=RECEIVED
   * @returns Frontend domain model with Date objects and extracted string values
   */
  static fromBackendDto(dto: ReceivedOrderBackendDto): ReceivedOrder {
    return {
      id: dto.id,
      examCode: dto.examCode,
      examName:
        typeof dto.examName === 'string'
          ? dto.examName
          : (dto.examName?.name ?? ''),
      orderNumber: dto.orderNumber,
      admissionDate: new Date(dto.admissionDate),
      admissionSite: this.extractNameField(dto.admissionSite),
      region: this.extractNameField(dto.region),
      identificationType: dto.identificationType ?? '',
      identificationNumber: dto.identificationNumber ?? '',
      patientFirstName: dto.patientFirstName,
      patientLastName: dto.patientLastName,
      birthDate: dto.birthDate ? new Date(dto.birthDate) : new Date(),
      priority:
        typeof dto.priority === 'string'
          ? dto.priority
          : (dto.priority?.name ?? ''),
      examType: this.extractNameField(dto.examType),
      isReprocess: dto.isReprocess,
      reprocessCause: dto.isReprocess ? (dto.reprocessCause ?? '') : '',
      reprocessObservation: dto.isReprocess
        ? (dto.reprocessObservation ?? '')
        : '',
      lgNumber: dto.lgNumber,
      specimenId: dto.lgNumber,
      caseNumber: dto.caseNumber,
      acmg: dto.acmg ?? false,
      permissions: {
        canEdit: true,
        canDelete: false,
        canView: true
      }
    };
  }

  /**
   * Maps array of backend DTOs to frontend models
   */
  static fromBackendDtoArray(dtos: ReceivedOrderBackendDto[]): ReceivedOrder[] {
    return dtos.map((dto) => this.fromBackendDto(dto));
  }

  /**
   * Extracts name field from nested object or returns string directly
   * Helper method to avoid nested ternary operations
   *
   * @param field String or object with name property (optional)
   * @returns Extracted name string or empty string
   */
  private static extractNameField(field?: string | { name: string }): string {
    if (!field) {
      return '';
    }
    return typeof field === 'string' ? field : field.name;
  }
}
